import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";

// Constants and Utils
import { CampaignTypesConstants } from "../../constants/GeneralConstants";
import { checkIsCoralMediaSpecific } from "../../common-utils/user-utils/userUtils";
import {
  convertISTtoUTC,
  getTimestampFromMomentDate,
} from "../../common-utils/date-utils/DateUtils";

// Components
import Spinner from "../spinner/Spinner";
import CheckBox from "../check-box/CheckBox";
import BootstrapDateRangePicker from "../bootstrap-date-range-picker/BootstrapDateRangePicker";

/**
 * Campaigns Filter
 */
export default function CampaignsFilter({
  containerClassName = "",
  className = "",
  isLoading = false,
  handleResetFilter = () => {},
  handleApplyFilter = () => {},
}) {
  const menuRef = useRef(null);

  // States
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [campaignTypes, setCampaignTypes] = useState([]);

  // click outside close menu
  useEffect(() => {
    function handleClickOutside(event) {
      const isClickInsideMenu =
        menuRef.current && menuRef.current.contains(event.target);

      const isClickInsideDatepicker =
        event.target.closest(".daterangepicker") !== null; // <-- this is key

      if (isOpen && !isClickInsideMenu && !isClickInsideDatepicker) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Style for DatePicker
  const stylesObject = {
    autoApply: true,
    border: true,
    buttonClassName: "col-12 px-0",
  };

  // coral media
  const isCoralMediaSpecific = checkIsCoralMediaSpecific();
  const durationSelectStyle = isCoralMediaSpecific ? "col-6" : "col-12";

  // Function
  function handleCampaignTypeChange(e, type) {
    const { checked } = e.target || {};

    if (checked) {
      // Add type if checked
      setCampaignTypes((prev) => [...prev, type]);
    } else {
      // Remove type if unchecked
      setCampaignTypes((prev) => prev.filter((ct) => ct !== type));
    }
  }

  // reset States
  function handleReset() {
    setIsOpen(false);
    setStartDate("");
    setEndDate("");
    setCampaignTypes([]);

    // callback
    handleResetFilter();
  }

  function handleApply() {
    // TS (IST)
    const startTS = getTimestampFromMomentDate(startDate);
    const endTS = getTimestampFromMomentDate(endDate);

    // TS (UTC)
    const startUTC = convertISTtoUTC(startTS);
    const endUTC = convertISTtoUTC(endTS);

    if ((startTS && !endTS) || (endTS && !startTS)) {
      toast.error("Please select both dates");
      return;
    }

    // close menu
    setIsOpen(false);

    // callback
    handleApplyFilter(startUTC, endUTC, campaignTypes);
  }

  // footer buttons data
  const footerButtons = [
    {
      label: "Close",
      onClick: () => setIsOpen(false),
      className: "btn-outline-primary",
    },
    {
      label: "Reset",
      onClick: handleReset,
      className: "btn-warning mx-3",
    },
    {
      label: "Apply",
      onClick: handleApply,
      className: "btn-primary",
    },
  ];

  return (
    <div
      ref={menuRef}
      className={`position-relative d-flex align-items-center ${containerClassName}`}
      data-html2canvas-ignore="true"
    >
      <button
        id="campaignFilter"
        title="Filter Campaigns"
        type="button"
        className={`btn ml-3 btn-outline-secondary shadow-none px-3 ${className}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        Filter Campaigns
        {isLoading ? (
          <Spinner className="spinner-border-sm ml-2" />
        ) : (
          <i className="fas fa-sliders-h ml-2"></i>
        )}
      </button>

      {isOpen && (
        <div
          className="dropdown-menu dropdown-menu-right show p-3 w-400"
          aria-labelledby="campaignFilter"
          onClick={(e) => e.stopPropagation()} // stops auto close on inside click
        >
          <div className="row">
            {/* Duration Select */}
            <div className={durationSelectStyle}>
              <h6 className="font-weight-bold">Select Duration</h6>
              <div className="d-flex flex-column">
                <BootstrapDateRangePicker
                  isSingleDatePicker={true}
                  initialStartDate={startDate}
                  onApplyDates={setStartDate}
                  placeHolder={"Start Date"}
                  styleObject={stylesObject}
                />
                <div className="mt-3">
                  <BootstrapDateRangePicker
                    isSingleDatePicker={true}
                    initialStartDate={endDate}
                    onApplyDates={setEndDate}
                    placeHolder={"End Date"}
                    styleObject={stylesObject}
                  />
                </div>
              </div>
            </div>

            {/* CampaignType */}
            {isCoralMediaSpecific && (
              <div className="col-6 px-0">
                <h6 className="font-weight-bold">Campaign Types</h6>
                {CampaignTypesConstants.map((type, i) => {
                  const { label } = type;
                  return (
                    <CheckBox
                      key={i}
                      checkBoxKey={label}
                      checked={campaignTypes.includes(label)}
                      onChange={handleCampaignTypeChange}
                      boxStyle="p-0 m-0 mb-1"
                      inputBoxStyle="m-0"
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer Buttons */}
          <div className="d-flex justify-content-end mt-4">
            {footerButtons.map((btnData, i) => {
              const { label, onClick, className } = btnData;
              return (
                <button
                  key={i}
                  className={`btn shadow-none ${className}`}
                  onClick={onClick}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
